import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate } from "react-router-dom";
import { useGameDispatch } from "../context/GameContext";
import SeoHead from "../components/SeoHead";
import { useState, useEffect } from "react";
import type { SiteData, BracketData } from "../types";

interface SavedGame {
  siteData: SiteData;
  brackets: BracketData;
  timestamp: string;
}

export default function GameHistoryPage() {
  const navigate = useNavigate();
  const dispatch = useGameDispatch();
  const [history, setHistory] = useState<SavedGame[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem("gameHistory");
    if (stored) {
      setHistory(JSON.parse(stored));
    }
  }, []);

  function handleLoad(game: SavedGame) {
    dispatch({
      type: "CONTINUE_GAME",
      payload: { siteData: game.siteData, brackets: game.brackets },
    });
    if (game.siteData.gameFinished) {
      navigate("/results");
    } else {
      navigate("/game/play");
    }
  }

  return (
    <Box id="history-page" sx={{ maxWidth: 700, mx: "auto", mt: 4 }}>
      <SeoHead title="Game History" description="View your past bracket games and results." canonical="/history" />
      <Typography id="history-title" variant="h4" gutterBottom>
        <HistoryIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Game History
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        View all of your past bracket games in one place. Revisit completed brackets to see your
        final rankings, or pick up an in-progress game right where you left off.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Your game history is saved locally in your browser, so your results stay with you across
        sessions. Click any game below to jump back into it — completed games will show your full
        results, and in-progress games will resume exactly where you left off. To save results to
        the cloud and share them publicly, sign in with Google from the results page.
      </Typography>

      {history.length === 0 ? (
        <Card id="history-empty" sx={{ mt: 3 }}>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="h6" color="text.secondary">
              No game history yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Play some games and they will appear here
            </Typography>
            <Button variant="contained" onClick={() => navigate("/")}>
              Start Playing
            </Button>
          </CardContent>
        </Card>
      ) : (
        history.map((game, index) => (
          <Card key={index} sx={{ mb: 2, cursor: "pointer" }} onClick={() => handleLoad(game)}>
            <CardContent sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="h6">
                  {game.siteData.gameMode} — {game.siteData.bracketSize} Bracket
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {game.siteData.gameType === "SingleE" ? "Single" : "Double"} Elimination
                  {game.siteData.artistSelected && ` — ${game.siteData.artistSelected}`}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {game.siteData.gameFinished ? "Completed" : "In Progress"}
              </Typography>
            </CardContent>
          </Card>
        ))
      )}

      {/* History info */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          About Game History
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Every bracket you play is automatically saved to your browser's local storage. This means
          your history persists even if you close the tab or restart your browser. Each entry shows
          the game mode, bracket size, elimination type, and whether the game is completed or still
          in progress.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Want to share your results with others? After completing a bracket, you can sign in with
          Google on the results page to save it to your account and make it visible on the Explore
          page. Cloud-saved brackets are accessible from any device, while local history stays on
          the browser where you played.
        </Typography>
      </Box>
    </Box>
  );
}
